import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { SchemaMarkup, SEOHead } from '../components/SEO/SchemaMarkup';
import { Button } from '../components/ui/button';
import { 
  Wrench, Clock, Shield, MapPin, Phone, CheckCircle, 
  ChevronRight, Star, Truck, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Programmatic SEO Page: /reparacion/{brand}/{model}/{problem}/{city}
const ProgrammaticSEOPage = () => {
  const { brand, model, problem, city } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPages, setRelatedPages] = useState([]);

  useEffect(() => {
    fetchPageData();
  }, [brand, model, problem, city]);

  const fetchPageData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/seo/programmatic-page`, {
        params: { brand, model, problem, city }
      });
      setPageData(response.data);
      setRelatedPages(response.data.related_pages || []);
    } catch (error) {
      console.error('Error fetching page data:', error);
      // Generate fallback content
      setPageData(generateFallbackData());
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackData = () => {
    const brandName = brand?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Dispositivo';
    const modelName = model?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
    const problemName = problem?.replace(/-/g, ' ') || 'reparación';
    const cityName = city?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'España';
    
    return {
      title: `Reparación ${brandName} ${modelName} - ${problemName} en ${cityName}`,
      h1: `Reparación de ${problemName} en ${brandName} ${modelName}`,
      subtitle: `Servicio técnico especializado en ${cityName}`,
      description: `Reparamos ${problemName} en tu ${brandName} ${modelName} en ${cityName}. Técnicos certificados, repuestos originales y garantía de 3 meses incluida.`,
      brand_name: brandName,
      model_name: modelName,
      problem_name: problemName,
      city_name: cityName,
      price_range: { min: 29, max: 149 },
      repair_time: '24-72 horas',
      warranty: '3 meses',
      features: [
        'Diagnóstico gratuito',
        'Repuestos originales',
        'Técnicos certificados',
        'Recogida a domicilio',
        'Seguimiento en tiempo real',
        'Garantía 3 meses'
      ],
      faqs: [
        {
          question: `¿Cuánto cuesta reparar ${problemName} en ${brandName} ${modelName}?`,
          answer: `El precio de reparación de ${problemName} en ${brandName} ${modelName} oscila entre 29€ y 149€ dependiendo de la gravedad del problema y los repuestos necesarios. Ofrecemos diagnóstico gratuito.`
        },
        {
          question: `¿Cuánto tiempo tarda la reparación?`,
          answer: `La mayoría de reparaciones de ${problemName} se completan en 24-72 horas. En casos complejos puede extenderse hasta 5 días laborables.`
        },
        {
          question: `¿Ofrecen recogida a domicilio en ${cityName}?`,
          answer: `Sí, ofrecemos recogida gratuita a domicilio en ${cityName} y alrededores. También puedes enviarnos tu dispositivo por mensajería con portes pagados.`
        },
        {
          question: `¿Qué garantía tienen las reparaciones?`,
          answer: `Todas nuestras reparaciones incluyen 6 meses de garantía completa que cubre tanto la mano de obra como los repuestos utilizados.`
        }
      ],
      steps: [
        { name: 'Solicita presupuesto', text: 'Rellena el formulario o llámanos para obtener un presupuesto sin compromiso.' },
        { name: 'Envío o recogida', text: 'Recogemos tu dispositivo a domicilio o puedes enviarlo por mensajería.' },
        { name: 'Diagnóstico', text: 'Nuestros técnicos diagnostican el problema y te confirman el presupuesto.' },
        { name: 'Reparación', text: 'Procedemos a reparar tu dispositivo con repuestos de calidad.' },
        { name: 'Entrega', text: 'Te devolvemos tu dispositivo reparado con garantía de 3 meses.' }
      ]
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0047FF]"></div>
      </div>
    );
  }

  const data = pageData || generateFallbackData();

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* SEO Head with Canonical and Hreflang */}
      <SEOHead
        title={data.title}
        description={data.description}
        canonical={`/reparacion/${brand}/${model}/${problem}/${city}`}
      />

      {/* Schema Markup - Multiple schemas for rich snippets */}
      <SchemaMarkup 
        type="RepairService" 
        data={{
          deviceName: `${data.brand_name} ${data.model_name}`,
          cityName: data.city_name,
          description: data.description,
          minPrice: data.price_range?.min,
          maxPrice: data.price_range?.max
        }}
      />
      <SchemaMarkup 
        type="FAQ" 
        data={{ faqs: data.faqs }}
      />
      <SchemaMarkup 
        type="HowTo" 
        data={{
          name: `Cómo reparar ${data.problem_name} en ${data.brand_name} ${data.model_name}`,
          description: data.description,
          steps: data.steps
        }}
      />
      <SchemaMarkup
        type="BreadcrumbList"
        data={{
          items: [
            { name: 'Inicio', url: 'https://rma.arreglatelo.es' },
            { name: 'Reparación', url: 'https://rma.arreglatelo.es/reparacion' },
            { name: data.brand_name, url: `https://rma.arreglatelo.es/reparacion/${brand}` },
            { name: data.model_name, url: `https://rma.arreglatelo.es/reparacion/${brand}/${model}` },
            { name: data.problem_name, url: `https://rma.arreglatelo.es/reparacion/${brand}/${model}/${problem}` },
            { name: data.city_name, url: `https://rma.arreglatelo.es/reparacion/${brand}/${model}/${problem}/${city}` }
          ]
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a2e] text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/reparacion" className="hover:text-white">Reparación</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/reparacion/${brand}`} className="hover:text-white">{data.brand_name}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{data.model_name}</span>
          </nav>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#0047FF]/20 text-[#0047FF] px-4 py-2 rounded-full text-sm mb-4">
                <MapPin className="w-4 h-4" />
                Servicio en {data.city_name}
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {data.h1}
              </h1>
              
              <p className="text-xl text-gray-300 mb-6">
                {data.subtitle}
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Clock className="w-5 h-5 text-[#0047FF]" />
                  <span>{data.repair_time}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span>Garantía {data.warranty}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span>4.8/5 (256 reseñas)</span>
                </div>
              </div>

              <div className="flex gap-4">
                <Link to="/warranty/new">
                  <Button className="bg-[#0047FF] hover:bg-[#003BCC] text-lg px-8 py-6">
                    Solicitar Reparación
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+34624473293">
                  <Button variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" />
                    Llamar Ahora
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">Precio Estimado</h2>
              <div className="text-5xl font-bold text-[#0047FF] mb-2">
                {data.price_range?.min}€ - {data.price_range?.max}€
              </div>
              <p className="text-gray-400 mb-6">Diagnóstico gratuito incluido</p>
              
              <ul className="space-y-3">
                {data.features?.slice(0, 6).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            ¿Cómo funciona la reparación?
          </h2>
          
          <div className="grid md:grid-cols-5 gap-4">
            {data.steps?.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="bg-[#F5F5F7] rounded-xl p-6 text-center h-full">
                  <div className="w-12 h-12 bg-[#0047FF] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {idx + 1}
                  </div>
                  <h3 className="font-semibold mb-2">{step.name}</h3>
                  <p className="text-sm text-gray-600">{step.text}</p>
                </div>
                {idx < data.steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2">
                    <ChevronRight className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-16 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Preguntas Frecuentes sobre {data.problem_name}
          </h2>
          
          <div className="space-y-4">
            {data.faqs?.map((faq, idx) => (
              <details key={idx} className="bg-white rounded-xl border overflow-hidden group">
                <summary className="p-6 cursor-pointer font-semibold flex items-center justify-between hover:bg-gray-50">
                  {faq.question}
                  <ChevronRight className="w-5 h-5 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Otros servicios de reparación
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {relatedPages.length > 0 ? relatedPages.map((page, idx) => (
              <Link key={idx} to={page.url} className="group">
                <div className="bg-[#F5F5F7] rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2 group-hover:text-[#0047FF]">
                    {page.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{page.description}</p>
                  <span className="text-[#0047FF] text-sm flex items-center gap-1">
                    Ver más <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            )) : (
              <>
                <Link to={`/reparacion/${brand}/${model}`} className="group">
                  <div className="bg-[#F5F5F7] rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <h3 className="font-semibold mb-2 group-hover:text-[#0047FF]">
                      Todas las reparaciones {data.model_name}
                    </h3>
                    <p className="text-sm text-gray-600">Ver todos los servicios disponibles</p>
                  </div>
                </Link>
                <Link to={`/reparacion/${brand}`} className="group">
                  <div className="bg-[#F5F5F7] rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <h3 className="font-semibold mb-2 group-hover:text-[#0047FF]">
                      Reparación {data.brand_name}
                    </h3>
                    <p className="text-sm text-gray-600">Todos los modelos {data.brand_name}</p>
                  </div>
                </Link>
                <Link to={`/reparacion-en/${city}`} className="group">
                  <div className="bg-[#F5F5F7] rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <h3 className="font-semibold mb-2 group-hover:text-[#0047FF]">
                      Reparación en {data.city_name}
                    </h3>
                    <p className="text-sm text-gray-600">Todos los servicios en tu ciudad</p>
                  </div>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#0047FF] to-[#0033CC] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Listo para reparar tu {data.brand_name} {data.model_name}?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Solicita tu reparación ahora y recibe diagnóstico gratuito
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/warranty/new">
              <Button className="bg-white text-[#0047FF] hover:bg-gray-100 text-lg px-8 py-6">
                Solicitar Reparación
              </Button>
            </Link>
            <a href="https://wa.me/34624473293" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgrammaticSEOPage;
